// WhatsApp Webhook Service
// Handles incoming WhatsApp messages and routes them for processing
// In production, this would be a server-side endpoint

const WEBHOOK_MESSAGES_KEY = 'hmgc_whatsapp_messages';
const WEBHOOK_CONFIG_KEY = 'hmgc_webhook_config';

// Store for incoming messages (simulated - in production would be server-side)
export const getIncomingMessages = () => {
  try {
    const messages = localStorage.getItem(WEBHOOK_MESSAGES_KEY);
    return messages ? JSON.parse(messages) : [];
  } catch (e) {
    console.error('Error reading incoming messages:', e);
    return [];
  }
};

export const saveIncomingMessages = (messages) => {
  try {
    localStorage.setItem(WEBHOOK_MESSAGES_KEY, JSON.stringify(messages));
    return true;
  } catch (e) {
    console.error('Error saving incoming messages:', e);
    return false;
  }
};

export const addIncomingMessage = (message) => {
  const messages = getIncomingMessages();
  const newMessage = {
    id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    timestamp: new Date().toISOString(),
    processed: false,
    ...message
  };
  messages.unshift(newMessage);
  // Keep only last 100 messages
  if (messages.length > 100) {
    messages.splice(100);
  }
  saveIncomingMessages(messages);
  return newMessage;
};

export const markMessageProcessed = (messageId, result) => {
  const messages = getIncomingMessages();
  const index = messages.findIndex(m => m.id === messageId);
  if (index !== -1) {
    messages[index].processed = true;
    messages[index].processedAt = new Date().toISOString();
    messages[index].result = result;
    saveIncomingMessages(messages);
  }
};

export const getUnprocessedMessages = () => {
  return getIncomingMessages().filter(m => !m.processed);
};

// Webhook configuration
export const getWebhookConfig = () => {
  try {
    const config = localStorage.getItem(WEBHOOK_CONFIG_KEY);
    return config ? JSON.parse(config) : {
      enabled: true,
      autoProcess: true,
      notifyOnNew: true
    };
  } catch (e) {
    return { enabled: true, autoProcess: true, notifyOnNew: true };
  }
};

export const saveWebhookConfig = (config) => {
  try {
    localStorage.setItem(WEBHOOK_CONFIG_KEY, JSON.stringify(config));
    return true;
  } catch (e) {
    return false;
  }
};

// Simulate receiving a WhatsApp message (for testing)
export const simulateIncomingMessage = (from, body, customerName = null) => {
  const message = addIncomingMessage({
    from: from,
    body: body,
    customerName: customerName,
    type: 'text',
    source: 'whatsapp',
    simulated: true
  });
  
  console.log('📥 Simulated incoming WhatsApp message:', message);
  return message;
};

// Parse Twilio webhook payload (for real webhook integration)
export const parseTwilioWebhook = (payload) => {
  return {
    from: payload.From?.replace('whatsapp:', '') || payload.from,
    body: payload.Body || payload.body,
    messageSid: payload.MessageSid || payload.messageSid,
    accountSid: payload.AccountSid || payload.accountSid,
    numMedia: parseInt(payload.NumMedia || '0'),
    mediaUrls: [],
    timestamp: new Date().toISOString()
  };
};

// Sample messages for testing/demo
export const SAMPLE_MESSAGES = [
  {
    from: '+1234567890',
    customerName: 'ABC Construction',
    body: 'Need pickup at 123 Main St, Houston TX tomorrow at 8am. Delivering to 456 Oak Ave, Dallas TX. 5000 lbs of steel beams, 20ft long.'
  },
  {
    from: '+1987654321',
    customerName: 'XYZ Materials',
    body: 'Urgent load! Pick up gravel from our yard at 789 Industrial Blvd, Austin. Drop at construction site 321 New Development, San Antonio. About 10 tons, need it there by 2pm today.'
  },
  {
    from: '+1555123456',
    customerName: 'Smith Lumber',
    body: 'Hey, can you pick up some lumber for me? Give me a sec to get the details...'
  },
  {
    from: '+1555987654',
    customerName: null, // Unknown customer
    body: 'Need a truck to move equipment from 100 Factory Rd to 200 Warehouse St. About 3000 kg, dimensions 10x4x4 feet.'
  },
  {
    from: '+1444555666',
    customerName: 'Johnson Concrete',
    body: 'Pickup concrete blocks at our plant. Delivery to 555 Building Site. Weight unknown, will confirm when driver arrives.'
  }
];

// Load sample messages for demo
export const loadSampleMessages = () => {
  const existing = getIncomingMessages();
  if (existing.length === 0) {
    SAMPLE_MESSAGES.forEach((msg, index) => {
      setTimeout(() => {
        simulateIncomingMessage(msg.from, msg.body, msg.customerName);
      }, index * 100);
    });
  }
  return SAMPLE_MESSAGES.length;
};

// Clear all messages
export const clearAllMessages = () => {
  saveIncomingMessages([]);
};

export default {
  getIncomingMessages,
  saveIncomingMessages,
  addIncomingMessage,
  markMessageProcessed,
  getUnprocessedMessages,
  getWebhookConfig,
  saveWebhookConfig,
  simulateIncomingMessage,
  parseTwilioWebhook,
  loadSampleMessages,
  clearAllMessages,
  SAMPLE_MESSAGES
};
